import { useCallback } from 'react';
import { MediaSegment } from 'redux/modules/entities';
import { removeGapsFromTime } from 'utils/embed/media-segment';
import { millisToSec, secToMillis } from 'utils/time';

type UseBufferedProps = {
  segments: MediaSegment[];
  videoRef: React.RefObject<HTMLVideoElement>;
};

/**
 * Before segmented media, there was a use case where the video element's max buffered
 * offset was used to show how much of the video was buffered in the UI.
 *
 * This hook helps support that use case.
 */
export default function useBuffered({ segments, videoRef }: UseBufferedProps) {
  const getMaxBufferedPlaybackPosition = useCallback(() => {
    if (!videoRef.current) {
      return 0;
    }

    const { buffered } = videoRef.current;

    if (buffered.length === 0) {
      return 0;
    }

    const endSec = buffered.end(buffered.length - 1);

    const displayEndMillis = removeGapsFromTime(secToMillis(endSec), segments);

    return millisToSec(displayEndMillis);
  }, [segments, videoRef]);

  return { getMaxBufferedPlaybackPosition };
}
